import React, { useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { view, bowser } from '@yola/ws-sdk';
import selectors from '../../../common/constants/selectors';
import attributes from '../constants/attributes';
import StretchTrigger from '../components/stretch-trigger';
import trackStretchCompositeItem from '../trackers/track-stretch-composite-item';

const MIN_ELEMENT_SIZE_FOR_INNER_TRIGGER = 67;
const SCREEN_OFFSET = 5;
const INNER_TRIGGER_OFFSET = 4;
const OUTER_TRIGGER_OFFSET = 14;

const getStretchTriggerPosition = (liveElement, scrollPosition) => {
  const { defaultView } = liveElement.ownerDocument;
  const { innerWidth } = defaultView;
  const rect = liveElement.getBoundingClientRect();

  const topPosition = rect.top + (bowser.ios ? 0 : scrollPosition);
  const innerEdgePosition = Math.max(
    SCREEN_OFFSET,
    Math.min(innerWidth - SCREEN_OFFSET, innerWidth - rect.right + INNER_TRIGGER_OFFSET)
  );
  const outerEdgePosition = Math.max(
    SCREEN_OFFSET,
    Math.min(innerWidth - SCREEN_OFFSET, rect.right + OUTER_TRIGGER_OFFSET)
  );

  const shouldUseOuterPosition =
    rect.width < MIN_ELEMENT_SIZE_FOR_INNER_TRIGGER ||
    rect.height < MIN_ELEMENT_SIZE_FOR_INNER_TRIGGER;

  return shouldUseOuterPosition
    ? {
        top: `${topPosition}px`,
        left: `${outerEdgePosition}px`,
        right: 'auto',
      }
    : {
        top: `${topPosition + INNER_TRIGGER_OFFSET}px`,
        left: 'auto',
        right: `${innerEdgePosition}px`,
      };
};

const StretchToolContainer = ({ elementId, scrollPosition }) => {
  const liveElement = view.accessors.getLiveElement(elementId);
  const isStretched = liveElement.hasAttribute(attributes.STRETCHED);
  const mediaElement = liveElement.querySelector(selectors.MEDIA_CONTAINER);
  const mediaElementId = view.accessors.getLiveElementId(mediaElement);

  const { top, left, right } = useMemo(
    () => getStretchTriggerPosition(liveElement, scrollPosition),
    [liveElement, scrollPosition]
  );

  const handleClick = useCallback(() => {
    trackStretchCompositeItem({ elementId, stretched: !isStretched });

    if (isStretched) {
      view.operations.removeElementAttribute(elementId, attributes.STRETCHED);
    } else {
      view.operations.setElementAttribute(elementId, attributes.STRETCHED, 'true');
    }

    requestAnimationFrame(() => {
      // Force programmatic selection (not user-triggered) to reopen all control pane triggers after stretching.
      // And since this action also tracks the `SITE_ELEMENT_CLICKED` event,
      // mark it as `forced` to avoid firing that event.
      view.operations.setSelectedElement(mediaElementId || elementId, {
        forced: true,
      });
    });
  }, [isStretched, mediaElementId, elementId]);

  return (
    <StretchTrigger
      topPosition={top}
      leftPosition={left}
      rightPosition={right}
      isStretched={isStretched}
      onMouseUp={handleClick}
    />
  );
};

StretchToolContainer.propTypes = {
  elementId: PropTypes.string.isRequired,
  scrollPosition: PropTypes.number.isRequired,
};

export default StretchToolContainer;
